import React, { useEffect, useMemo, useState } from 'react';
import { Bubble, Button, Card, Dropdown, Form, Icon, Input, List } from 'tea-component';
import './index.scss';
import {isNumber, useDblIntl} from '@/pages/datalab/utils/utils';
import type { IColumnInfo, IDimensionRange } from '@dbl/core/dataSource';

export const DimensionType = {
  default: 'default', // 离散数字
  range: 'range', // 自定义区间
  nolyAnalysis: 'nolyAnalysis', // 只参与计算，界面不展示，从保存中返回现场时需过滤掉
};

export function DimensionSelect(props: { column: IColumnInfo; notify: Function; }) {
  const { column, notify } = props;
  const { dblFormatMessage: explorationFormatMessage, commonFormatMessage, isEn} = useDblIntl("exploration");
  const initRanges = [
    {
      end: '0',
    },
    {
      begin: '0',
    },
  ];

  const [ranges, setRanges] = useState<IDimensionRange[]>([]);

  const DimensionTypes = useMemo(
    () => [
      {
        type: DimensionType.default,
        text: explorationFormatMessage("discrete"),
      },
      {
        type: DimensionType.range,
        text: explorationFormatMessage("custom_region"),
      },
    ],
    [ranges, isEn],
  );

  const DimensionOptions = useMemo(() => {
    const options: any = [];
    switch (column.dimension_method) {
      case 1: // 离散数字
        options.push(DimensionTypes[0]);
        break;
      case 2: // 离散数字+自定义区间
        options.push(DimensionTypes[0], DimensionTypes[1]);
        break;
    }
    return options;
  }, [column]);

  const [dimensionType, setDimensionType] = useState<string>();
  const buttonContent = useMemo(
    () => DimensionTypes.find((item) => item.type === column.dimension_type)?.text ?? '',
    [dimensionType, isEn],
  );

  useEffect(() => {
    if (!column.dimension_type) {
      notify(DimensionOptions?.[0]?.type);
    }
    setDimensionType(column.dimension_type ?? '');
  }, [column]);

  const rangeEndisNull = (end?: string) => !end || end === '-';

  // 输入值为空值,或者左则数值大于右侧数值,不能提交
  const confirmButtonDisable = useMemo(() => {
    let disable = false;
    try {
      ranges.forEach((range, index) => {
        if (
          (index !== ranges.length - 1 && rangeEndisNull(range.end)) ||
          (range.begin && range.end && Number(range.begin) > Number(range.end))
        ) {
          disable = true;
          throw new Error();
        }
      });
    } catch (e) {}
    return disable;
  }, [ranges]);

  return (
    <Dropdown
      boxClassName="explore-analysis-dimension-select"
      button={buttonContent}
      appearance="button"
      clickClose={false}
      onOpen={() => {
        setRanges(column.dimension_rangs?.map((item) => ({ ...item })) ?? initRanges);
      }}
    >
      {(close) => (
        <List type="option">
          {(DimensionOptions || []).map((option: any) => (
            <>
              {option.type === 'default' ? (
                <List.Item
                  onClick={() => {
                    setDimensionType(DimensionTypes[0].type);
                    notify(DimensionTypes[0].type);
                    close();
                  }}
                  selected={column.dimension_type === 'default'}
                >
                  {explorationFormatMessage("discrete")}
                </List.Item>
              ) : null}
              {option.type === 'range' ? (
                <List.SubMenu
                  label={explorationFormatMessage("custom_region")}
                  placement="right-end"
                  trigger="click"
                  selected={column.dimension_type === 'range'}
                >
                  <Card className="explore-analysis-dimension-select-rang-dialog">
                    <Card.Body>
                      <div className="item_title">
                        <span>{explorationFormatMessage("region")}</span>
                        <Button
                          className='btn-down'
                          type="link"
                          onClick={() => {
                            setRanges(initRanges);
                          }}
                        >
                          {commonFormatMessage("clear")}
                        </Button>
                      </div>
                      <div className="item_body">
                        {(ranges || []).map((range, index) => (
                          <div className="item">
                            <span className="left_name">{`${explorationFormatMessage("region")}${index + 1}`}</span>
                            <span className="left_bracket">{index === 0 ? '(' : '['}</span>
                            <Input
                              className="input"
                              // type={`${index === 0 ? '' : 'number'}`}
                              readonly
                              value={range.begin ?? `-∞`}
                            />
                            <div style={{ width: 20, textAlign: 'center' }}>,</div>
                            <Form.Control
                              showStatusIcon={false}
                              status={`${
                                range.end === '-' || Number(range.begin) > Number(range.end) ? 'error' : 'success'
                              }`}
                            >
                              <Input
                                className="input"
                                // type={`${index === ranges.length - 1 ? '' : 'number'}`}
                                readonly={index === ranges.length - 1}
                                value={range.end ?? '+∞'}
                                onChange={(value) => {
                                  if (value && !isNumber(value)) {
                                    return;
                                  }
                                  setRanges((ranges) => {
                                    // eslint-disable-next-line no-param-reassign
                                    ranges[index].end = value;
                                    // eslint-disable-next-line no-param-reassign
                                    ranges[index + 1].begin = value;
                                    return [...ranges];
                                  });
                                }}
                              />
                            </Form.Control>
                            {`\xa0)`}
                            <Icon
                              className={`icon icon_hover ${index === ranges.length - 1 ? 'hide' : ''}`}
                              type="plus"
                              onClick={() => {
                                setRanges((ranges) => {
                                  ranges.splice(index + 1, 0, {
                                    begin: ranges?.[index]?.end,
                                    end: ranges?.[index + 1]?.begin,
                                  });
                                  return [...ranges];
                                });
                              }}
                            />
                            <Icon
                              className={`icon icon_hover ${index === 0 || index === ranges.length - 1 ? 'hide' : ''}`}
                              type="close"
                              size="s"
                              onClick={() => {
                                setRanges((ranges) => {
                                  ranges.splice(index, 1);
                                  // eslint-disable-next-line no-param-reassign
                                  ranges[index].begin = ranges[index - 1].end;
                                  return [...ranges];
                                });
                              }}
                            />
                            <Bubble
                              content={`${rangeEndisNull(range.end) ? explorationFormatMessage("custom_region_bubble1") : explorationFormatMessage("custom_region_bubble2")}`}
                              placement={'right'}
                              error
                            >
                              <Icon
                                className={`icon ${
                                  (index !== ranges.length - 1 && rangeEndisNull(range.end)) ||
                                  (index !== 0 &&
                                    index !== ranges.length - 1 &&
                                    Number(range.begin) > Number(range.end))
                                    ? ''
                                    : 'hide'
                                }`}
                                type="error"
                              />
                            </Bubble>
                          </div>
                        ))}
                      </div>
                    </Card.Body>
                    <Card.Footer className="footer">
                      <Button
                        type="primary"
                        disabled={confirmButtonDisable}
                        onClick={() => {
                          setDimensionType(DimensionTypes[1].type);
                          notify(DimensionTypes[1].type, ranges);
                          close();
                        }}
                      >
                        {commonFormatMessage("save")}
                      </Button>
                      <Button onClick={close}>{commonFormatMessage("cancel")}</Button>
                    </Card.Footer>
                  </Card>
                </List.SubMenu>
              ) : null}
            </>
          ))}
        </List>
      )}
    </Dropdown>
  );
}
